import { useMutation } from '@apollo/client';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { CompanyHeader } from 'components/CompanyHeader/CompanyHeader';
import { CompanySendWelcomeEmailModal } from 'components/CompanySendWelcomeEmailModal/CompanySendWelcomeEmailModal';
import { CompanyTabs } from 'components/CompanyTabs/CompanyTabs';
import { Icon } from 'components/Icon/Icon';
import { useToast } from 'components/Toast/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { COMPANY_SEND_WELCOME_EMAIL } from 'services/companies/mutation';
import { useCompanyStore } from 'stores/company.store';

import { CompanyInfoPreview } from './_components/CompanyInfoPreview/CompanyInfoPreview';
import { CompanyMembersPreview } from './_components/CompanyMembersPreview/CompanyMembersPreview';
import { CompanyProfilePreview } from './_components/CompanyProfilePreview/CompanyProfilePreview';

export const CompanyProfile = () => {
    const { t } = useTranslation('translation');
    const { t: c } = useTranslation('company');
    const company = useCompanyStore(state => state.company);
    const navigate = useNavigate();
    const { id } = useParams();
    const { toast } = useToast();
    const [showWelcomeEmailModal, setShowWelcomeEmailModal] = useState(false);
    const [sendWelcomeEmail, { loading }] = useMutation(COMPANY_SEND_WELCOME_EMAIL);

    const editCompany = () => {
        if (!id) return;
        navigate(AppRoutes.editCompany.replace(':id', id));
    };

    const handleSendWelcomeEmail = () => {
        if (!id) return;

        sendWelcomeEmail({
            variables: { company: id },
        })
            .then(res => {
                if (res.data?.companySendWelcomeEmail?.success) {
                    toast({
                        title: c('welcomeEmailSuccess'),
                        description: res.data.companySendWelcomeEmail.message,
                        variant: 'success',
                    });
                    setShowWelcomeEmailModal(false);
                } else {
                    handleError();
                }
            })
            .catch(error => {
                const errorMessage = error.message || c('welcomeEmailError');
                toast({
                    title: c('failToastTitle'),
                    description: errorMessage,
                    variant: 'warning',
                });
                setShowWelcomeEmailModal(false);
            });
    };

    const handleError = () => {
        toast({
            title: c('failToastTitle'),
            description: c('failToastText'),
            variant: 'warning',
        });
    };

    return (
        <>
            <Breadcrumbs crumbs={[t('breadcrumbs.members'), t('breadcrumbs.companies'), company?.name]} />
            <CompanyHeader>
                {company && (
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setShowWelcomeEmailModal(true)}>
                            <Icon name="mail" className="h-4 mr-2" />
                            {c('sendWelcomeEmailButton')}
                        </Button>
                        <Button onClick={editCompany}>
                            <Icon name="edit" className="h-4 mr-2" />
                            Edit
                        </Button>
                    </div>
                )}
            </CompanyHeader>
            <CompanyTabs />

            <div className="flex flex-col gap-6">
                <div className="flex gap-6">
                    <CompanyProfilePreview />
                    <CompanyInfoPreview />
                </div>
                <CompanyMembersPreview />
            </div>

            <CompanySendWelcomeEmailModal
                isOpen={showWelcomeEmailModal}
                onClose={() => setShowWelcomeEmailModal(false)}
                onSend={handleSendWelcomeEmail}
                loading={loading}
            />
        </>
    );
};
